import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { pctraderApi } from '../services/api';

const fmtFlex = (n, maxDp = 7) => n == null ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: maxDp });
const fmtNet = (n) => {
  if (n == null) return '—';
  const val = Number(n);
  const abs = Math.abs(val);
  if (abs === 0) return '0.0000000';
  if (abs < 1e-7) {
    const dp = Math.max(7, -Math.floor(Math.log10(abs)) + 2);
    return val.toFixed(dp);
  }
  return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 7 });
};
const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${min}`;
};

const chainLabel = (c) => c ? c.charAt(0).toUpperCase() : '—';

const botLabel = (b) => {
  const map = { sniper: 'S', mean_reversion: 'MR', volume_momentum: 'VM', utility: 'U' };
  if (map[b]) return map[b];
  if (!b) return '—';
  return b.split(/[\s_]+/).map(w => w[0].toUpperCase()).join('');
};
const plCls = (v) => ({ PROFIT: 'text-green-600 bg-green-50', LOSS: 'text-red-600 bg-red-50', FORCED: 'text-orange-600 bg-orange-50' }[v] || 'text-gray-600 bg-gray-50');
const tabCls = (active) => `px-4 py-2 text-sm rounded-md font-medium transition-colors ring-1 ${active ? 'bg-primary-100 text-primary-700 ring-primary-200' : 'bg-white text-gray-600 hover:bg-gray-100 ring-gray-200'}`;

const PCTrader = () => {
  const [trades, setTrades] = useState([]);
  const [watchPairs, setWatchPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('trades');
  const [tradeFilter, setTradeFilter] = useState('all');
  const [filterBot, setFilterBot] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterChain, setFilterChain] = useState('');
  const [tradePage, setTradePage] = useState(1);
  const PAGE_SIZE = 20;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [tradesRes, watchRes] = await Promise.allSettled([
        pctraderApi.getTrades(),
        pctraderApi.getWatchPairs(),
      ]);
      if (tradesRes.status === 'fulfilled') setTrades(tradesRes.value.data);
      if (watchRes.status === 'fulfilled') setWatchPairs(watchRes.value.data);
      if (tradesRes.status === 'rejected' && watchRes.status === 'rejected') {
        setError('Failed to load Bot Trader data.');
      }
    } catch {
      setError('Failed to load Bot Trader data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openTrades = trades.filter(t => t.status === 'open');
  const closedTrades = trades.filter(t => t.status !== 'open');

  const chainSummaryMap = {};
  trades.forEach(t => {
    const c = t.chain || '—';
    if (!chainSummaryMap[c]) chainSummaryMap[c] = { open: 0, spent: 0, wins: 0, losses: 0, net: 0 };
    const s = chainSummaryMap[c];
    if (t.status === 'open') {
      s.open++;
      s.spent += Number(t.pls_spent) || 0;
    } else {
      if (t.profit_or_loss === 'PROFIT') s.wins++;
      if (t.profit_or_loss === 'LOSS') s.losses++;
      s.net += Number(t.net_pls_from_sale) || 0;
    }
  });
  const chainRows = Object.entries(chainSummaryMap).sort(([a], [b]) => a.localeCompare(b));


  const botOptions = [...new Set(trades.map(t => botLabel(t.bot_type)).filter(b => b !== '—'))].sort();
  const symbolOptions = [...new Set(trades.map(t => t.symbol).filter(Boolean))].sort();
  const chainOptions = [...new Set(trades.map(t => t.chain).filter(Boolean))].sort();

  const filteredTrades = (tradeFilter === 'open' ? openTrades : tradeFilter === 'closed' ? closedTrades : trades)
    .filter(t => !filterBot || botLabel(t.bot_type) === filterBot)
    .filter(t => !filterSymbol || t.symbol === filterSymbol)
    .filter(t => !filterChain || t.chain === filterChain);
  const totalPages = Math.ceil(filteredTrades.length / PAGE_SIZE);
  const pagedTrades = filteredTrades.slice((tradePage - 1) * PAGE_SIZE, tradePage * PAGE_SIZE);

  const applyToBot = { volume: 'volume_momentum', reversion: 'mean_reversion' };
  const groupedWatchPairs = Object.values(
    watchPairs.reduce((acc, p) => {
      const key = `${p.symbol}||${p.chain || ''}`;
      if (!acc[key]) acc[key] = { ...p, bots: [] };
      acc[key].bots.push(botLabel(applyToBot[p.applies_to] || p.applies_to));
      return acc;
    }, {})
  ).sort((a, b) => a.symbol.localeCompare(b.symbol));


  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Bot Trader</h1>
          <button
            onClick={load}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors ring-1 ring-gray-200"
            title="Refresh"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Summary table — per chain */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-4 inline-block min-w-64">
          <table className="text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide">Chain</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide">Open</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">Deployed</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide">W</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide">L</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chainRows.map(([chain, s]) => (
                <tr key={chain} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-semibold text-gray-800 text-xs md:text-base">{chain}</td>
                  <td className="px-4 py-2 text-right text-gray-700 text-xs md:text-base">{s.open || '—'}</td>
                  <td className="px-4 py-2 text-right font-mono text-gray-600 text-xs md:text-base hidden sm:table-cell">{s.spent ? fmtFlex(s.spent) : '—'}</td>
                  <td className="px-4 py-2 text-right font-semibold text-green-600 text-xs md:text-base">{s.wins || '—'}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-600 text-xs md:text-base">{s.losses || '—'}</td>
                  <td className={`px-4 py-2 text-right font-mono font-semibold text-xs md:text-base ${s.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {s.net !== 0 ? `${s.net >= 0 ? '+' : ''}${fmtNet(s.net)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setTab('trades')} className={tabCls(tab === 'trades')}>
            Trades ({trades.length})
          </button>
          <button onClick={() => setTab('watchpairs')} className={tabCls(tab === 'watchpairs')}>
            Watch Pairs ({watchPairs.length})
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : tab === 'trades' ? (
          <>
            {/* Trade filters */}
            <div className="flex gap-2 mb-3 flex-wrap items-center">
              {['all', 'open', 'closed'].map(f => (
                <button key={f} onClick={() => { setTradeFilter(f); setTradePage(1); }}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ring-1 ${tradeFilter === f ? 'bg-primary-100 text-primary-700 ring-primary-200' : 'bg-white text-gray-500 hover:bg-gray-100 ring-gray-200'}`}>
                  {f.charAt(0).toUpperCase() + f.slice(1)} {f === 'all' ? `(${trades.length})` : f === 'open' ? `(${openTrades.length})` : `(${closedTrades.length})`}
                </button>
              ))}
              <select value={filterBot} onChange={e => { setFilterBot(e.target.value); setTradePage(1); }}
                className="px-2 py-1 text-xs rounded-full ring-1 ring-gray-200 bg-white text-gray-600 focus:outline-none focus:ring-primary-300">
                <option value="">All Bots</option>
                {botOptions.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={filterSymbol} onChange={e => { setFilterSymbol(e.target.value); setTradePage(1); }}
                className="px-2 py-1 text-xs rounded-full ring-1 ring-gray-200 bg-white text-gray-600 focus:outline-none focus:ring-primary-300">
                <option value="">All Symbols</option>
                {symbolOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterChain} onChange={e => { setFilterChain(e.target.value); setTradePage(1); }}
                className="px-2 py-1 text-xs rounded-full ring-1 ring-gray-200 bg-white text-gray-600 focus:outline-none focus:ring-primary-300">
                <option value="">All Chains</option>
                {chainOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {(filterBot || filterSymbol || filterChain) && (
                <button onClick={() => { setFilterBot(''); setFilterSymbol(''); setFilterChain(''); setTradePage(1); }}
                  className="px-2 py-1 text-xs rounded-full ring-1 ring-red-200 bg-red-50 text-red-600 hover:bg-red-100">
                  Clear
                </button>
              )}
            </div>
            {filteredTrades.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No trades found</div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Symbol</th>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Bot</th>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Chain</th>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">DEX</th>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Status</th>
                        <th className="text-right px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Spent</th>
                        <th className="text-right px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Received</th>
                        <th className="text-right px-2 md:px-4 py-3 font-medium text-gray-600">Net</th>
                        <th className="text-right px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Gas</th>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Trigger</th>
                        <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Dates</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pagedTrades.map((t) => (
                        <tr key={t.id} className="hover:bg-gray-50">
                          <td className="px-2 md:px-4 py-3 font-medium text-gray-900 text-xs md:text-sm">{t.symbol || '—'}</td>
                          <td className="px-2 md:px-4 py-3">
                            <span className="px-1.5 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700">{botLabel(t.bot_type)}</span>
                          </td>
                          <td className="px-2 md:px-4 py-3">
                            <span className="px-1.5 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700">{chainLabel(t.chain)}</span>
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">{t.dex || '—'}</td>
                          <td className="px-2 md:px-4 py-3">
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${t.status === 'open' ? 'bg-blue-50 text-blue-700' : plCls(t.profit_or_loss)}`}>
                              <span className="md:hidden">{t.status === 'open' ? 'O' : { PROFIT: 'P', LOSS: 'L', FORCED: 'F' }[t.profit_or_loss] ?? 'C'}</span>
                              <span className="hidden md:inline">{t.status === 'open' ? 'Open' : t.profit_or_loss || 'Closed'}</span>
                            </span>
                          </td>
                          <td className="px-2 md:px-4 py-3 text-right font-mono text-gray-700 text-xs md:text-sm hidden md:table-cell">{t.pls_spent ? fmtFlex(t.pls_spent) : '—'}</td>
                          <td className="px-2 md:px-4 py-3 text-right font-mono text-gray-700 text-xs md:text-sm hidden md:table-cell">{t.pls_received_from_sale ? fmtFlex(t.pls_received_from_sale) : '—'}</td>
                          {(() => {
                            let net = t.net_pls_from_sale != null ? Number(t.net_pls_from_sale) : null;
                            if ((net === null || net === 0) && t.pls_received_from_sale > 0 && t.pls_spent != null) {
                              net = Number(t.pls_received_from_sale) - Number(t.pls_spent);
                            }
                            return (
                              <td className={`px-2 md:px-4 py-3 text-right font-mono font-medium text-xs md:text-sm ${net == null ? 'text-gray-400' : net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {net != null ? `${net >= 0 ? '+' : ''}${fmtNet(net)}` : '—'}
                              </td>
                            );
                          })()}
                          <td
                            className="px-2 md:px-4 py-3 text-right font-mono text-gray-500 text-xs cursor-default hidden md:table-cell"
                            title={(t.buy_gas_pls != null || t.sell_gas_pls != null)
                              ? `Buy gas: ${fmtFlex(t.buy_gas_pls ?? 0)}\nSell gas: ${fmtFlex(t.sell_gas_pls ?? 0)}`
                              : undefined}
                          >
                            {(t.buy_gas_pls != null || t.sell_gas_pls != null)
                              ? fmtFlex((t.buy_gas_pls || 0) + (t.sell_gas_pls || 0))
                              : '—'}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">{t.trigger_for_sale || '—'}</td>
                          <td className="px-2 md:px-4 py-3 hidden md:table-cell">
                            {(t.buy_transaction_date || t.sale_transaction_date) ? (
                              <div className="relative group inline-block">
                                <span className="cursor-default text-gray-400 hover:text-gray-600 select-none">📅</span>
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-10 pointer-events-none">
                                  <div className="bg-gray-900 text-white text-xs rounded px-2 py-1.5 whitespace-nowrap shadow-lg">
                                    <div>Buy: {fmtDate(t.buy_transaction_date) || '—'}</div>
                                    <div>Sell: {fmtDate(t.sale_transaction_date) || '—'}</div>
                                  </div>
                                </div>
                              </div>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 px-1">
                <span className="text-xs text-gray-400">
                  {(tradePage - 1) * PAGE_SIZE + 1}–{Math.min(tradePage * PAGE_SIZE, filteredTrades.length)} of {filteredTrades.length}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => setTradePage(p => Math.max(1, p - 1))} disabled={tradePage === 1}
                    className="px-2 py-1 text-xs rounded ring-1 ring-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default">
                    ‹ Prev
                  </button>
                  <span className="px-2 py-1 text-xs text-gray-500">{tradePage} / {totalPages}</span>
                  <button onClick={() => setTradePage(p => Math.min(totalPages, p + 1))} disabled={tradePage === totalPages}
                    className="px-2 py-1 text-xs rounded ring-1 ring-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default">
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </>
        ) : tab === 'watchpairs' ? (
          watchPairs.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No watch pairs found</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Symbol</th>
                      <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Bot</th>
                      <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600">Chain</th>
                      <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Source</th>
                      <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Token Address</th>
                      <th className="text-left px-2 md:px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Added</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupedWatchPairs.map((p) => (
                      <tr key={`${p.symbol}||${p.chain}`} className="hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-3 font-medium text-gray-900 text-xs md:text-sm">{p.symbol}</td>
                        <td className="px-2 md:px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {p.bots.map(b => (
                              <span key={b} className="px-1.5 py-0.5 rounded-full text-xs bg-primary-50 text-primary-700">{b}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-3 text-gray-600 text-xs">{p.chain || '—'}</td>
                        <td className="px-2 md:px-4 py-3 hidden md:table-cell">
                          <span className={`px-1.5 py-0.5 rounded-full text-xs ${p.written_by === 'monitor' ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'}`}>
                            {p.written_by}
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-3 font-mono text-xs text-gray-400 hidden md:table-cell">{p.token_address || '—'}</td>
                        <td className="px-2 md:px-4 py-3 text-xs text-gray-400 hidden md:table-cell">{fmtDate(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : null}

      </div>
    </div>
  );
};

export default PCTrader;
